import React, { ComponentType, ReactNode, useEffect } from 'react';
import {
  MemoryRouter,
  MemoryRouterProps,
  Route,
  RouterProvider,
  Routes,
  BrowserRouter,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { createBrowserRouter } from '@bloomays-lib/common.monitoring/browser';
import { LoaderGlobal, NotFound } from '@bloomays-lib/ui.shared';

import Bucket from '../components/templates/Bucket';
import BucketLogin from '../components/templates/BucketLogin';

import ProtectedRoute from '../auth/ProtectedRoute';

import Dashboard from '../components/pages/Dashboard';
import DashboardRedirect from '../components/pages/DashboardRedirect';
import FreelanceMissionsDashboard from '../components/pages/FreelanceMissionsDashboard';
import Settings from '../components/pages/Settings';
import SatisfactionClient from '../components/pages/SatisfactionClient';
import ElectronicSign from '../components/pages/ElectronicSign';
import BloomerInvitation from '../components/pages/BloomerInvitation';
import MissionDetails from '../components/pages/MissionDetails';
import BillingActivity from '../components/pages/BillingActivity';
import Finance from '../components/pages/Finance';
import File from '../components/pages/File';
import Error from '../components/pages/Error';
import EditCRA from '../components/pages/EditCRA';
import MissionChoice from '../components/pages/MissionChoice';
import CRAManagement from '../components/pages/CRAManagement';
import BloomerPersonnalInformations from '../components/pages/BloomerPersonalInformations';
import BloomerSociety from '../components/pages/BloomerSociety';
import LoginFormBloomer from '../components/pages/LoginFormBloomer';
import TalentView from '../components/pages/TalentView';
import TalentSearch from '../components/pages/TalentSearch';
import EmailNotVerified from '../components/pages/EmailNotVerified';
import FallbackConnection from '../components/pages/FallbackConnection';
import JobFunnelProject from '../components/pages/JobFunnelProject';
import JobFunnelProjectDashboard from '../components/pages/JobFunnelProjectDashboard';
import EmbedCRAInviteClient from '../components/pages/EmbedCRAInviteClient';
import AdminSkillManagment from '../components/pages/AdminSkillManagment';
import AdminJobTitleManagment from '../components/pages/AdminJobTitleManagment';
import AdminDirectJobTitleManagment from '../components/pages/AdminDirectJobTitleManagment';
import MissionPlanningDashboard from '../components/pages/MissionPlanningDashboard';
import MonthlyActivityDashboard from '../components/pages/MonthlyActivityDashboard';
import MissionContracts from '../components/pages/MissionContracts';
import Contracts from '../components/pages/Contracts';
import Primes from '../components/pages/Primes';
import PrimesIndexRedirect from '../components/pages/PrimesIndexRedirect';
import PrimesMonthlyPage from '../components/pages/PrimesMonthlyPage';
import PrimesAnnualPage from '../components/pages/PrimesAnnualPage';
import TamMargesDashboard from '../components/pages/TamMargesDashboard';
import CLevelTamAnnualDetailPage from '../components/pages/CLevelTamAnnualDetailPage';
import PurchaseOrderDashboard from '../components/pages/PurchaseOrderDashboard';
import AuthInfo from '../components/molecules/AuthInfo';
import ViewSignedCRA from '../components/pages/ViewSignedCRA';
import { Logger } from '../services/serviceLogger';

const logger = Logger('RouterWrapper');
const getProtectedComponent = (component: ComponentType): React.JSX.Element => {
  return <ProtectedRoute Component={component} />;
};

/**
 * Redirige les anciens liens mission scopés sur une mission (/missions/:id[/...]) vers le nouvel
 * espace admin (Bloomies) /missions-freelance/:id[/...], afin de conserver les liens déjà émis
 * (Slack, e-mails…) avec un fil d'Ariane cohérent ("Missions freelance / Détail / …").
 */
const RedirectToBloomiesMissions = (): React.JSX.Element => {
  return <Navigate to={`/mission`} replace />;
};

const RedirectToBloomiesDashboard = (): React.JSX.Element => {
  return <Navigate to={`/dashboard`} replace />;
};
export const getRouteConfig = () => {
  const router = createBrowserRouter([
    {
      path: '/',
      element: <Bucket showFooter={true} />,
      children: [
        { index: true, element: getProtectedComponent(DashboardRedirect) },
        { path: 'dashboard', element: getProtectedComponent(Dashboard) },
        {
          path: 'missions-freelance',
          children: [
            { index: true, element: getProtectedComponent(FreelanceMissionsDashboard) },
            { path: ':id', element: getProtectedComponent(MissionDetails) },
            { path: ':id/activities', element: getProtectedComponent(MonthlyActivityDashboard) },
            { path: ':id/contracts', element: getProtectedComponent(MissionContracts) },
          ],
        },
        { path: 'planningMission', element: getProtectedComponent(MissionPlanningDashboard) },
        { path: 'settings', element: getProtectedComponent(Settings) },
        { path: 'satisfaction/:month/:recordId/:id', element: getProtectedComponent(SatisfactionClient) },
        { path: 'signing/:id', element: getProtectedComponent(EmbedCRAInviteClient) },
        {
          path: 'jobs',
          children: [
            { index: true, element: getProtectedComponent(JobFunnelProjectDashboard) },
            { path: ':id', element: getProtectedComponent(JobFunnelProject) },
          ],
        },
        {
          path: 'electronicSign',
          children: [
            {
              path: ':externalDocumentId',
              element: getProtectedComponent(ElectronicSign),
              children: [{ path: ':signerId', element: getProtectedComponent(ElectronicSign) }],
            },
          ],
        },
        { path: 'invite/:token', element: getProtectedComponent(BloomerInvitation) },
        // Le détail mission et ses vues filtrées sur une mission (activités, contrats) sont
        // désormais servis sous /missions-freelance/:id (espace Bloomies). On conserve
        // /missions/:id[/...] en redirection pour les liens déjà diffusés.
        // L'édition d'un CRA (/missions/:id/cra/:date) reste sous /missions : elle fait partie
        // du parcours Bloomer de gestion des CRA (« Gérer les CRA », /mission/:id).
        { path: 'missions/*', element: <RedirectToBloomiesMissions /> },
        { path: 'missions', element: <RedirectToBloomiesMissions /> },
        {
          path: 'mission',
          children: [
            { index: true, element: getProtectedComponent(MissionChoice) },
            {
              path: ':missionRecordId',
              children: [
                { index: true, element: getProtectedComponent(CRAManagement) },
                { path: 'cra/:date', element: getProtectedComponent(EditCRA) },
                { path: '*', element: <RedirectToBloomiesMissions /> },
                {
                  path: 'society',
                  children: [
                    { index: true, element: <RedirectToBloomiesDashboard /> },
                    { path: 'new', element: <RedirectToBloomiesDashboard /> },
                    { path: ':societyRecordId', element: getProtectedComponent(BloomerSociety) },
                    { path: '*', element: <RedirectToBloomiesDashboard /> },
                  ],
                },
              ],
            },
            { path: '*', element: <RedirectToBloomiesMissions /> },
          ],
        },
        { path: 'billingsActivity', element: getProtectedComponent(BillingActivity) },
        { path: 'activities', element: getProtectedComponent(MonthlyActivityDashboard) },
        { path: 'contracts', element: getProtectedComponent(Contracts) },
        {
          path: 'primes',
          element: getProtectedComponent(Primes),
          children: [
            { index: true, element: getProtectedComponent(PrimesIndexRedirect) },
            { path: 'mois', element: getProtectedComponent(PrimesMonthlyPage) },
            { path: 'annuel', element: getProtectedComponent(PrimesAnnualPage) },
            { path: 'dashboard/details', element: getProtectedComponent(CLevelTamAnnualDetailPage) },
            { path: 'dashboard', element: getProtectedComponent(TamMargesDashboard) },
            { path: ':mmYyyy', element: getProtectedComponent(PrimesMonthlyPage) },
          ],
        },
        { path: 'bdc', element: getProtectedComponent(PurchaseOrderDashboard) },
        { path: 'finance', element: getProtectedComponent(Finance) },
        {
          path: 'talentsV2',
          children: [
            { index: true, element: getProtectedComponent(TalentSearch) },
            { path: ':id', element: getProtectedComponent(TalentView) },
          ],
        },
        {
          path: 'talents',
          children: [
            { index: true, element: getProtectedComponent(TalentSearch) },
            { path: ':id', element: getProtectedComponent(TalentView) },
          ],
        },
        { path: 'error', element: getProtectedComponent(Error) },
        { path: 'me/informations', element: getProtectedComponent(BloomerPersonnalInformations) },
        {
          path: 'society',
          children: [
            { index: true, element: <RedirectToBloomiesDashboard /> },
            { path: 'new', element: <RedirectToBloomiesDashboard /> },
            { path: 'new/mission/:missionRecordId', element: getProtectedComponent(BloomerSociety) },
            { path: ':recordId', element: getProtectedComponent(BloomerSociety) },
            { path: ':recordId/mission/:missionRecordId', element: getProtectedComponent(BloomerSociety) },
            { path: '*', element: <RedirectToBloomiesDashboard /> },
          ],
        },
        { path: '*', element: <NotFound /> },
        {
          path: 'admin',
          children: [
            { path: 'skills', element: getProtectedComponent(AdminSkillManagment) },
            { path: 'jobTitle', element: getProtectedComponent(AdminJobTitleManagment) },
            { path: 'directJobTitle', element: getProtectedComponent(AdminDirectJobTitleManagment) },
          ],
        },
      ],
    },
    {
      path: 'forbidden',
      element: <Bucket showFooter={false} />,
      children: [
        { index: true, element: <FallbackConnection /> },
        { path: 'email-not-verified', element: <EmailNotVerified /> },
        { path: 'not-recognized', element: <FallbackConnection /> },
        { path: '*', element: <FallbackConnection /> },
      ],
    },
    {
      path: 'login',
      element: <BucketLogin />,
      children: [{ index: true, element: <LoginFormBloomer /> }],
    },
    { path: 'files/:path/*', element: getProtectedComponent(File) },
    { path: 'public', children: [{ path: 'cra/:id/cra.pdf', element: <ViewSignedCRA /> }] },
  ]);
  return router;
};

const ResetScrollWrapper = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to the top of the page when the route changes
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    logger.debug('ResetScrollWrapper', { path: location.pathname });
  }, [location]);

  return children;
};

export const RouterWrapper = ({
  children,
  injectedRouter,
}: {
  children: ReactNode;
  injectedRouter?: MemoryRouterProps;
}) => {
  if (injectedRouter) {
    return (
      <div data-testid="router">
        <MemoryRouter {...injectedRouter}>
          <Routes>
            <Route path="/dashboard" element={<AuthInfo />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          {children}
        </MemoryRouter>
      </div>
    );
  }
  return (
    <>
      <RouterProvider
        router={getRouteConfig()}
        fallbackElement={<LoaderGlobal waitingText="Chargement des routes" />}
      />
      <BrowserRouter>
        <ResetScrollWrapper>{children}</ResetScrollWrapper>
      </BrowserRouter>
    </>
  );
};

export default RouterWrapper;
